import type CanvasConnection from '@gl/lit/components/base/canvas_connection/index.js';
import {isCanvasConnectionType} from '@gl/lit/types/canvas.js';
import type {Nullable} from '@gl/lit/types/general.js';
import {isPlainObject} from '@gl/lit/utils/index.js';

type WalkerCache = {
  previous: Nullable<IWalkerTarget[]>;
};

const INITIAL_CACHE: WalkerCache = Object.freeze({
  previous: null,
});

/**
 * Walk up through previous nodes given a starting point.
 */
export class CanvasWalker {
  private cache: WalkerCache = {...INITIAL_CACHE};

  constructor(private start: IWalkerTarget) {
    this.start = start;
  }

  hasPrevious(node: IWalkerTarget): boolean {
    if (!this.previousSteps().length) return false;

    for (const previous of this.previousSteps()) {
      if (previous.isSameNode(node)) {
        return true;
      }

      if (previous.walker.previousSteps().length) {
        return previous.walker.hasPrevious(node as IWalkerTarget);
      }
    }

    return false;
  }

  previousSteps(): IWalkerTarget[] {
    if (this.cache.previous) return this.cache.previous;
    if (!this.start.model.inputs.size) return [];

    this.cache.previous = Array.from(this.start.model.inputs)
      .filter(element => {
        return (
          isCanvasConnectionType(element) &&
          element.model.source &&
          isWalkerTarget(element.model.source)
        );
      })
      .map(connection => {
        return (connection as CanvasConnection).model.source as IWalkerTarget;
      });

    return this.cache.previous;
  }

  clear() {
    this.cache = {...INITIAL_CACHE};
  }
}

function isWalkerTarget(node: Node): node is IWalkerTarget {
  return (
    'model' in node &&
    isPlainObject(node.model) &&
    'inputs' in node.model &&
    node.model.inputs instanceof Set
  );
}

export interface IWalkerTarget extends Node {
  walker: CanvasWalker;
  readonly model: {inputs: Set<HTMLElement>};
}
